import { type CustomerListItem, type CustomerListQuery } from '@utazzo/shared';
import { UzBadge, UzStatusBadge } from '@utazzo/ui';
import { UserPen } from 'lucide-react';
import React from 'react';
import { useNavigate } from 'react-router-dom';

import { listCustomers } from '../customers.api';
import { useBulkSetCustomersStatus, useCustomersList, useDeleteCustomer } from '../use-customers';

import { type DataGridColumn, type DataGridFilter } from '@/components/data-grid';
import { ListScreen, type ListScreenColumns } from '@/components/list-screen';
import { useCanExportFinanceData, useCurrentUser } from '@/features/auth/use-auth';

const KIND_OPTIONS = [
  { value: '', label: 'All kinds' },
  { value: 'INDIVIDUAL', label: 'Individual' },
  { value: 'CORPORATE', label: 'Corporate' },
] as const;

const STATUS_OPTIONS = [
  { value: '', label: 'Active + inactive' },
  { value: 'active', label: 'Active only' },
  { value: 'inactive', label: 'Inactive only' },
] as const;

/** Server search fires only from this many characters. */
const MIN_SEARCH_LENGTH = 3;

const kindLabel: Record<string, string> = {
  INDIVIDUAL: 'Individual',
  CORPORATE: 'Corporate',
};

/**
 * Format a mobile as "+<code> <number>" with exactly one leading "+". The stored
 * `mobileCountry` sometimes already includes the "+" (e.g. "+91"), which used to
 * render as "++91"; strip any leading "+" before prefixing.
 */
const formatMobile = (
  mobileCountry: string | null | undefined,
  mobileNumber: string | null | undefined,
): string => {
  if (!mobileNumber) return '';
  const code = (mobileCountry ?? '').replace(/^\++/, '');
  return `+${code} ${mobileNumber}`.trim();
};

/**
 * Returns a compact identity summary string (PAN / passport / GST) for display
 * in the Identity column. Prefers PAN → passport → GST. Returns '-' when none.
 */
const identitySummary = (row: CustomerListItem): string => {
  // CustomerListItem doesn't expose identity numbers directly (kept off the
  // list schema to reduce payload). Show a dash - the detail page has them.
  void row;
  return '-';
};

const FILTERS: ReadonlyArray<DataGridFilter> = [
  {
    id: 'kind',
    label: 'Kind',
    options: KIND_OPTIONS as ReadonlyArray<{ value: string; label: string }>,
    widthClass: 'w-44',
  },
  {
    id: 'status',
    label: 'Status',
    options: STATUS_OPTIONS as ReadonlyArray<{ value: string; label: string }>,
    widthClass: 'w-48',
  },
];

export const CustomersListPage = (): React.JSX.Element => {
  const me = useCurrentUser();
  const canExportFinance = useCanExportFinanceData();
  const canDelete = me?.role === 'ADMIN';
  const navigate = useNavigate();
  const deleteCustomer = useDeleteCustomer();
  const bulkSetStatusMut = useBulkSetCustomersStatus();

  const columns: ListScreenColumns<CustomerListItem> = ({ state }) => {
    const cols: ReadonlyArray<DataGridColumn<CustomerListItem>> = [
      {
        id: 'serial',
        header: 'S.No',
        width: '64px',
        cell: (_row, index) => (
          <span className="text-zinc-500">{(state.page - 1) * state.pageSize + index + 1}.</span>
        ),
      },
      {
        id: 'displayName',
        header: 'Name',
        sortable: true,
        cell: (row) => (
          <div className="flex flex-col gap-0.5">
            <span className="font-medium text-zinc-900">{row.displayName}</span>
            <UzBadge tone={row.kind === 'INDIVIDUAL' ? 'secondary' : 'default'} pill>
              {kindLabel[row.kind] ?? row.kind}
            </UzBadge>
          </div>
        ),
      },
      {
        id: 'contact',
        header: 'Contact',
        cell: (row) => (
          <div className="flex flex-col text-sm">
            <span className="text-zinc-700">{row.email ?? '-'}</span>
            <span className="text-zinc-500">
              {row.mobileNumber ? formatMobile(row.mobileCountry, row.mobileNumber) : '-'}
            </span>
          </div>
        ),
      },
      {
        id: 'identity',
        header: 'Identity',
        cell: (row) => <span className="text-sm text-zinc-500">{identitySummary(row)}</span>,
      },
      {
        id: 'status',
        header: 'Status',
        cell: (row) => <UzStatusBadge status={row.isActive ? 'active' : 'inactive'} />,
      },
    ];
    return cols;
  };

  return (
    <ListScreen<CustomerListItem, CustomerListQuery>
      title="Customers"
      description="CRM customer master. Search by name, email, mobile, or passport number."
      useList={useCustomersList}
      defaultSort={{ by: 'createdAt', dir: 'desc' }}
      defaultPageSize={20}
      minSearchLength={MIN_SEARCH_LENGTH}
      toQuery={(state, base) => {
        const { search, ...rest } = base;
        const status = state.filters.status;
        const kind = state.filters.kind;
        return {
          ...rest,
          ...(search ? { q: search } : {}),
          ...(kind === 'INDIVIDUAL' || kind === 'CORPORATE' ? { kind } : {}),
          ...(status === 'active' ? { isActive: true } : {}),
          ...(status === 'inactive' ? { isActive: false } : {}),
        };
      }}
      columns={columns}
      filters={FILTERS}
      getRowId={(row) => row.id}
      onRowClick={(row) => navigate(`/customers/${row.id}`)}
      createButton={{ label: 'Add customer', onClick: () => navigate('/customers/new') }}
      rowActions={{
        extra: (row) => [
          {
            type: 'item',
            label: 'Edit',
            icon: <UserPen className="h-4 w-4" />,
            onSelect: () => navigate(`/customers/${row.id}`),
          },
        ],
        delete: {
          onDelete: (row) => deleteCustomer.mutateAsync(row.id),
          disabled: () => !canDelete,
          confirm: (row) => ({
            title: 'Delete this customer?',
            description: `${row.displayName} will be soft-deleted and hidden from lists. Their bookings and leads are unaffected.`,
          }),
        },
      }}
      bulkActions={
        canDelete
          ? {
              setActive: {
                onSetActive: async (rows, isActive) =>
                  bulkSetStatusMut.mutateAsync({ ids: rows.map((r) => r.id), isActive }),
                confirm: (rows, isActive) => ({
                  title: `${isActive ? 'Activate' : 'Deactivate'} ${rows.length} ${rows.length === 1 ? 'customer' : 'customers'}?`,
                  description: isActive
                    ? 'They will appear as active customers and be eligible for new bookings.'
                    : 'They will be marked inactive and hidden from active customer lists.',
                }),
              },
            }
          : undefined
      }
      export={{
        fetchPage: (q) => listCustomers(q),
        filename: 'customers',
        canExport: canExportFinance,
        csv: [
          { header: 'Name', value: (r) => r.displayName },
          { header: 'Kind', value: (r) => r.kind },
          { header: 'Email', value: (r) => r.email ?? '' },
          {
            header: 'Mobile',
            value: (r) => formatMobile(r.mobileCountry, r.mobileNumber),
          },
          { header: 'Company', value: (r) => r.companyName ?? '' },
          { header: 'GST number', value: (r) => r.gstNumber ?? '' },
          { header: 'Nationality', value: (r) => r.nationality?.name ?? '' },
          { header: 'Branch', value: (r) => r.branch?.name ?? '' },
          { header: 'Status', value: (r) => (r.isActive ? 'Active' : 'Inactive') },
          { header: 'Created at', value: (r) => r.createdAt },
        ],
      }}
      copy={{
        searchPlaceholder: 'Search by name, email, or mobile (min 3 characters)',
        emptyTitle: 'No customers match these filters',
        emptyDescription: 'Adjust the kind / status filter or clear the search box.',
        errorTitle: 'Failed to load customers',
        errorDescription: 'The server returned an error. Try refreshing or check your network.',
      }}
    />
  );
};
